import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "./actions";
import { showAlert } from "../../config/utils";
import Portal from "../common/components/Portal";
import { DynamicSoftCostModal } from "./components/DynamicSoftCostModal";
import ConfirmationModal from "../common/components/ConfirmationModal";

const useSoftCostDynamicField = ({ id, props }) => {
    const dispatch = useDispatch();
    const { saveDynamicSoftCostField, updateDynamicSoftCostField, deleteDynamicSoftCostField } = useSelector(state => state.softCostReducer);
    const [dynamicSoftCosts, setDynamicSoftCosts] = useState([{ key: "construction_contingency", name: "Construction Contingency" }]);
    const [showDynamicAddModal, setDynamicAddModal] = useState(false);
    const [submittingDynamicSc, setSubmittingDynamicSc] = useState(false);
    const [showDeleteConfirmation, setDeleteConfirmation] = useState(false);
    const [selectedDynamicField, setSelectedDynamicField] = useState({});
    const [refreshToggle, setRefreshToggle] = useState(false);

    const prevSaveDynamicSoftCostField = useRef(saveDynamicSoftCostField);
    const prevUpdateDynamicSoftCostField = useRef(updateDynamicSoftCostField);
    const prevDeleteDynamicSoftCostField = useRef(deleteDynamicSoftCostField);

    useEffect(() => {
        if (saveDynamicSoftCostField && prevSaveDynamicSoftCostField.current !== saveDynamicSoftCostField) {
            const { success, message = "", error = "", data = {} } = saveDynamicSoftCostField;
            prevSaveDynamicSoftCostField.current = saveDynamicSoftCostField;
            if (success === true) {
                setRefreshToggle(!refreshToggle);
                showAlert(message || "Field Created Successfully");
            } else if (success === false) {
                showAlert(message || error || "Oops... Failed to create dynamic field!");
            }
            setSubmittingDynamicSc(false);
            setDynamicAddModal(false);
            setSelectedDynamicField(null);
        }
    }, [saveDynamicSoftCostField]);

    useEffect(() => {
        if (updateDynamicSoftCostField && prevUpdateDynamicSoftCostField.current !== updateDynamicSoftCostField) {
            const { success, message = "", error = "", data = {} } = updateDynamicSoftCostField;
            prevUpdateDynamicSoftCostField.current = updateDynamicSoftCostField;
            if (success === true) {
                setDynamicSoftCosts(prevSf => prevSf.map(item => (item.id === data.id ? { ...item, name: data.label } : item)));
                showAlert(message || "Field Updated Successfully");
            } else if (success === false) {
                showAlert(message || error || "Oops... Failed to Update Dynamic Field!");
            }
            setSubmittingDynamicSc(false);
            setDynamicAddModal(false);
            setSelectedDynamicField(null);
        }
    }, [updateDynamicSoftCostField]);

    useEffect(() => {
        if (deleteDynamicSoftCostField && prevDeleteDynamicSoftCostField.current !== deleteDynamicSoftCostField) {
            const { success, message = "", error = "", data = {} } = deleteDynamicSoftCostField;
            prevDeleteDynamicSoftCostField.current = deleteDynamicSoftCostField;
            if (success === true) {
                setDynamicSoftCosts(prevSf => prevSf.filter(item => item.id !== selectedDynamicField.id));
                showAlert(message || "Field Deleted Successfully");
            } else if (success === false) {
                showAlert(message || error || "Oops... Failed to Delete Dynamic Field!");
            }
            setDeleteConfirmation(false);
            setSelectedDynamicField(null);
        }
    }, [deleteDynamicSoftCostField]);

    const handleAddDynamicSoftCost = data => {
        const { name } = data;
        const key = name?.toLowerCase()?.split(" ").join("_");
        if (
            dynamicSoftCosts
                .filter(elem => elem.key !== selectedDynamicField?.key)
                .map(elem => elem.name)
                ?.includes(name)
        ) {
            showAlert("Field already exists!");
        } else {
            const sc = { name, key };
            handleSaveDynamicField(sc);
        }
    };

    const onDynamicFieldAdd = field => {
        setSelectedDynamicField(null);
        setDynamicAddModal(true);
    };

    const onDynamicFieldEdit = field => {
        setSelectedDynamicField(field);
        setDynamicAddModal(true);
    };

    const onDynamicFieldDelete = field => {
        setDeleteConfirmation(true);
        setSelectedDynamicField(field);
    };

    const handleSaveDynamicField = async sc => {
        setSubmittingDynamicSc(true);
        let data = {};
        if (selectedDynamicField?.id) {
            data = {
                id: selectedDynamicField?.id,
                label: sc.name,
                last_updator_id: localStorage.getItem("userId")
            };
            dispatch(actions.updateDynamicSoftCostField(data));
        } else {
            data = {
                client_id: props.clientId || "",
                project_id: id || "",
                label: sc.name,
                key: sc.key,
                actual_key: sc.key,
                last_updator_id: localStorage.getItem("userId")
            };
            dispatch(actions.saveDynamicSoftCostField(data));
        }
    };

    const handleDeleteDynamicField = async sc => {
        if (selectedDynamicField?.id) {
            const params = {};
            dispatch(actions.deleteDynamicSoftCostField(selectedDynamicField.id, params));
        }
    };

    const renderDynamicSoftCostModal = () => {
        if (!showDynamicAddModal) return null;
        return (
            <Portal
                body={
                    <DynamicSoftCostModal
                        selectedDynamicField={selectedDynamicField}
                        onCancel={() => setDynamicAddModal(false)}
                        onFilterSave={data => handleAddDynamicSoftCost(data)}
                        isSubmitting={submittingDynamicSc}
                    />
                }
                onCancel={() => setDynamicAddModal(false)}
            />
        );
    };

    const renderDeleteConfirmationModal = () => {
        if (!showDeleteConfirmation) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={"Do you want to delete this dynamic soft cost?"}
                        message={"This action cannot be reverted, are you sure that you need to delete?"}
                        onNo={() => setDeleteConfirmation(false)}
                        onYes={() => {
                            handleDeleteDynamicField();
                        }}
                    />
                }
                onCancel={() => setDeleteConfirmation(false)}
            />
        );
    };
    return {
        dynamicSoftCosts,
        setDynamicSoftCosts,
        onDynamicFieldAdd,
        onDynamicFieldEdit,
        onDynamicFieldDelete,
        renderDynamicSoftCostModal,
        renderDeleteConfirmationModal,
        refreshToggle
    };
};

export default useSoftCostDynamicField;
